import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useState, useEffect, useRef, useCallback } from "react";
import { View, Text, StyleSheet, Pressable, Platform, ActivityIndicator } from "react-native";

import { decodePolyline } from "./decode-polyline";

import type { TravelTimeResult } from "@/lib/api";
import { fetchTravelTime } from "@/lib/api";
import { logRocketRedactProps } from "@/lib/logrocket";
import { ensureMapsLoaded } from "@/lib/maps-loader";
import { RNMapView, Marker, type RNMapViewInstance } from "@/lib/native-maps";
import { useSkipRule } from "@/lib/recorder/useSkipRule";
import { colors, fonts, spacing, radius } from "@/lib/theme";

interface TravelMapProps {
    /** Patient location */
    patientLat: number | null;
    patientLng: number | null;
    patientName?: string;
    /** Visit ID for the travel-time API call */
    visitId: string;
    /** Map height in pixels */
    height?: number;
}

type TransportMode = "driving" | "transit" | "walking";

const TRANSPORT_MODES: Array<{
    mode: TransportMode;
    label: string;
    icon: "car" | "bus" | "walk";
}> = [
    { mode: "driving", label: "Drive", icon: "car" },
    { mode: "transit", label: "Transit", icon: "bus" },
    { mode: "walking", label: "Walk", icon: "walk" }
];

const MAP_STYLES = [
    { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#bdbdbd" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#ececec" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#e8f0f8" }] }
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GoogleMap = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GoogleMaps = any;

export function TravelMap({
    patientLat,
    patientLng,
    patientName,
    visitId,
    height = 180
}: TravelMapProps) {
    const [activeMode, setActiveMode] = useState<TransportMode | null>(null);
    const [results, setResults] = useState<Record<string, TravelTimeResult>>({});
    const [loading, setLoading] = useState<Record<string, boolean>>({});
    const [rnLocation, setRnLocation] = useState<{ lat: number; lng: number } | null>(null);

    // Visit Recording — pause capture while a map is rendered. react-native-maps
    // tiles render off-screen on Android and don't snapshot reliably.
    useSkipRule("map_open");

    // Web map refs
    const mapRef = useRef<View>(null);
    const mapInstanceRef = useRef<GoogleMap>(null);
    const routeLineRef = useRef<unknown>(null);
    const rnMarkerRef = useRef<unknown>(null);

    // Native map refs
    const nativeMapRef = useRef<RNMapViewInstance | null>(null);

    // Require real, finite numbers — not just non-null. A NaN or stringified
    // coordinate passes `!= null` but makes Google Maps throw
    // "InvalidValueError: not a LatLng or LatLngLiteral" (TASKHEALTH-MOBILE-2F).
    const hasLocation = Number.isFinite(patientLat) && Number.isFinite(patientLng);
    const MAPS_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY ?? "";
    const NativeMapView = Platform.OS === "web" ? null : RNMapView;

    // ─── Web: Initialize Google Maps ─────────────────────
    useEffect(() => {
        if (Platform.OS !== "web" || !MAPS_KEY || !hasLocation) {
            return;
        }

        const initMap = () => {
            const el = mapRef.current as unknown as HTMLElement;
            if (!el) {
                return;
            }
            const g = (window as unknown as Record<string, unknown>).google as GoogleMaps;
            if (!g?.maps) {
                return;
            }

            const map = new g.maps.Map(el, {
                center: { lat: patientLat, lng: patientLng },
                zoom: 15,
                disableDefaultUI: true,
                zoomControl: false,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                clickableIcons: false,
                styles: MAP_STYLES,
                gestureHandling: "greedy"
            });
            mapInstanceRef.current = map;

            // Patient marker
            new g.maps.Marker({
                position: { lat: patientLat, lng: patientLng },
                map,
                icon: {
                    path: g.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: "#EF4444",
                    fillOpacity: 1,
                    strokeColor: "#FFFFFF",
                    strokeWeight: 2
                },
                title: patientName ?? "Patient"
            });
        };

        void ensureMapsLoaded().then(() => setTimeout(initMap, 100));
    }, [MAPS_KEY, hasLocation, patientLat, patientLng, patientName]);

    // ─── Web: Draw route when result changes ─────────────
    useEffect(() => {
        if (Platform.OS !== "web" || !mapInstanceRef.current || !activeMode) {
            return;
        }
        const result = results[activeMode];
        if (!result?.polyline) {
            return;
        }

        const g = (window as unknown as Record<string, unknown>).google as GoogleMaps;
        if (!g?.maps) {
            return;
        }
        const map = mapInstanceRef.current;

        // Clear old route line
        if (routeLineRef.current) {
            (routeLineRef.current as { setMap: (m: null) => void }).setMap(null);
        }
        // Clear old RN marker
        if (rnMarkerRef.current) {
            (rnMarkerRef.current as { setMap: (m: null) => void }).setMap(null);
        }

        // Decode polyline
        const path = decodePolyline(result.polyline).map((p) => ({
            lat: p.latitude,
            lng: p.longitude
        }));

        // Draw route line
        const line = new g.maps.Polyline({
            path,
            geodesic: true,
            strokeColor: "#45D2B0",
            strokeOpacity: 0.9,
            strokeWeight: 4
        });
        line.setMap(map);
        routeLineRef.current = line;

        // RN marker
        if (rnLocation) {
            const rnMarker = new g.maps.Marker({
                position: { lat: rnLocation.lat, lng: rnLocation.lng },
                map,
                icon: {
                    path: g.maps.SymbolPath.CIRCLE,
                    scale: 7,
                    fillColor: "#45D2B0",
                    fillOpacity: 1,
                    strokeColor: "#FFFFFF",
                    strokeWeight: 2
                },
                title: "You"
            });
            rnMarkerRef.current = rnMarker;
        }

        // Fit bounds to show entire route
        const bounds = new g.maps.LatLngBounds();
        path.forEach((p: { lat: number; lng: number }) => bounds.extend(p));
        if (rnLocation) {
            bounds.extend({ lat: rnLocation.lat, lng: rnLocation.lng });
        }
        map.fitBounds(bounds, 40);
    }, [activeMode, results, rnLocation]);

    // ─── Handle transport button tap ─────────────────────
    const handleTransportTap = useCallback(
        async (mode: TransportMode) => {
            // If already loaded, just switch
            if (results[mode]) {
                setActiveMode(mode);
                return;
            }

            setLoading((prev) => ({ ...prev, [mode]: true }));
            try {
                // Backend reads RN's home address from rn_addresses table — no GPS needed
                const result = await fetchTravelTime(visitId, mode);
                setResults((prev) => ({ ...prev, [mode]: result }));
                // Set RN location from the response (for map rendering)
                if (result.rnLat && result.rnLng) {
                    setRnLocation({ lat: result.rnLat, lng: result.rnLng });
                }
                setActiveMode(mode);
            } catch {
                // Silently fail — button stays at "--"
            }
            setLoading((prev) => ({ ...prev, [mode]: false }));
        },
        [visitId, rnLocation, results]
    );

    // ─── Get decoded route for native polyline ───────────
    const activeResult = activeMode ? results[activeMode] : null;
    const nativeRouteCoords = activeResult?.polyline ? decodePolyline(activeResult.polyline) : [];

    // ─── Fit native map when route loaded ────────────────
    useEffect(() => {
        if (Platform.OS === "web" || !nativeMapRef.current || nativeRouteCoords.length === 0) {
            return;
        }
        const coords = [...nativeRouteCoords];
        if (rnLocation) {
            coords.push({ latitude: rnLocation.lat, longitude: rnLocation.lng });
        }
        nativeMapRef.current.fitToCoordinates(coords, {
            edgePadding: { top: 40, right: 40, bottom: 40, left: 40 },
            animated: true
        });
    }, [nativeRouteCoords, rnLocation]);

    // ─── Render ──────────────────────────────────────────

    // Dynamically import Polyline for native (avoid web crash)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let NativePolyline: any = null;
    if (Platform.OS !== "web") {
        try {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            NativePolyline = require("react-native-maps").Polyline;
        } catch {
            // Not available
        }
    }

    return (
        <View>
            {/* Map */}
            {hasLocation ? (
                Platform.OS === "web" && MAPS_KEY ? (
                    <View
                        ref={mapRef}
                        style={[styles.mapContainer, { height }]}
                        {...logRocketRedactProps()}
                    />
                ) : NativeMapView && Marker ? (
                    <NativeMapView
                        {...logRocketRedactProps()}
                        ref={nativeMapRef}
                        style={[styles.mapContainer, { height }]}
                        initialRegion={{
                            latitude: patientLat ?? 0,
                            longitude: patientLng ?? 0,
                            latitudeDelta: 0.01,
                            longitudeDelta: 0.01
                        }}
                        scrollEnabled={true}
                        zoomEnabled={true}
                        pitchEnabled={false}
                        rotateEnabled={false}
                        toolbarEnabled={false}
                        showsCompass={false}
                    >
                        {/* Patient marker */}
                        <Marker
                            coordinate={{
                                latitude: patientLat ?? 0,
                                longitude: patientLng ?? 0
                            }}
                            title={patientName ?? "Patient"}
                            pinColor="#EF4444"
                        />

                        {/* RN marker */}
                        {rnLocation ? (
                            <Marker
                                coordinate={{
                                    latitude: rnLocation.lat,
                                    longitude: rnLocation.lng
                                }}
                                title="You"
                                pinColor="#45D2B0"
                            />
                        ) : null}

                        {/* Route polyline */}
                        {NativePolyline && nativeRouteCoords.length > 0 ? (
                            <NativePolyline
                                coordinates={nativeRouteCoords}
                                strokeColor="#45D2B0"
                                strokeWidth={4}
                            />
                        ) : null}
                    </NativeMapView>
                ) : (
                    <View style={[styles.mapPlaceholder, { height }]}>
                        <Text style={styles.placeholderText}>Map not available</Text>
                    </View>
                )
            ) : (
                <View style={[styles.mapPlaceholder, { height }]}>
                    <Text style={styles.placeholderText}>Address not geocoded</Text>
                </View>
            )}

            {/* Transport buttons */}
            <View style={styles.transportRow}>
                {TRANSPORT_MODES.map(({ mode, label, icon }) => {
                    const isActive = activeMode === mode;
                    const time = results[mode]?.durationText;
                    const isLoading = loading[mode];

                    return (
                        <Pressable
                            key={mode}
                            onPress={() => handleTransportTap(mode)}
                            style={({ pressed }) => [
                                styles.transportButton,
                                isActive && styles.transportButtonActive,
                                pressed && styles.transportPressed
                            ]}
                        >
                            <MaterialCommunityIcons
                                name={icon}
                                size={22}
                                color={isActive ? colors.teal600 : colors.textSecondary}
                            />
                            {isLoading ? (
                                <ActivityIndicator
                                    size="small"
                                    color={colors.teal400}
                                    style={styles.transportSpinner}
                                />
                            ) : (
                                <Text
                                    style={[
                                        styles.transportTime,
                                        isActive && styles.transportTimeActive
                                    ]}
                                >
                                    {time ?? "--"}
                                </Text>
                            )}
                            <Text
                                style={[
                                    styles.transportLabel,
                                    isActive && styles.transportLabelActive
                                ]}
                            >
                                {label}
                            </Text>
                        </Pressable>
                    );
                })}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    mapContainer: {
        borderRadius: radius.md,
        overflow: "hidden"
    },
    mapPlaceholder: {
        borderRadius: radius.md,
        backgroundColor: colors.gray100,
        alignItems: "center",
        justifyContent: "center"
    },
    placeholderText: {
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.textTertiary
    },
    transportRow: {
        flexDirection: "row",
        gap: spacing.sm,
        marginTop: spacing.md
    },
    transportButton: {
        flex: 1,
        alignItems: "center",
        paddingVertical: spacing.md,
        borderRadius: radius.md,
        borderWidth: 1.5,
        borderColor: colors.border,
        backgroundColor: colors.surface
    },
    transportButtonActive: {
        borderColor: colors.teal200,
        backgroundColor: colors.teal50
    },
    transportPressed: {
        backgroundColor: colors.gray100
    },
    transportSpinner: {
        marginTop: spacing.xs
    },
    transportTime: {
        fontFamily: fonts.bold,
        fontSize: 13,
        color: colors.textTertiary,
        marginTop: spacing.xs
    },
    transportTimeActive: {
        color: colors.teal600
    },
    transportLabel: {
        fontFamily: fonts.medium,
        fontSize: 10,
        color: colors.textTertiary,
        marginTop: 2
    },
    transportLabelActive: {
        color: colors.teal600
    }
});
